import { useState } from 'react';
import { Button, Card, FieldError, Flex, Spinner, TextField } from '@/components/common';
import type { SimpleTextFieldProps } from './RadixProps';

export interface AutocompleteOption {
  id: string;
  label: string;
}

export interface AutocompleteFieldProps extends SimpleTextFieldProps {
  value: string;
  options: AutocompleteOption[];
  onSelectOption: (option: AutocompleteOption) => void;
  /** Shows a spinner in the field's right slot (e.g. while a remote search runs). */
  loading?: boolean;
  error?: string;
  /** When true, Enter selects the first visible option instead of submitting a surrounding form. */
  selectOnEnter?: boolean;
}

/**
 * Text field with a dropdown of selectable options. Owns open/close state:
 * the dropdown renders while the field is focused and `options` is non-empty.
 * Callers control the input value and the options list.
 */
export function AutocompleteField({
  value,
  onChange,
  onBlur,
  onFocus,
  options,
  onSelectOption,
  loading,
  error,
  selectOnEnter,
  ...rest
}: AutocompleteFieldProps) {
  const [isFocused, setIsFocused] = useState(false);
  const open = isFocused && options.length > 0;

  return (
    <Flex direction="column" gap="1" position="relative">
      <TextField.Root
        {...rest}
        value={value}
        aria-autocomplete="list"
        aria-expanded={open}
        onChange={event => onChange(event.target.value)}
        onKeyDown={event => {
          if (selectOnEnter && event.key === 'Enter' && open) {
            event.preventDefault();
            onSelectOption(options[0]);
          }
        }}
        onFocus={() => {
          setIsFocused(true);
          onFocus?.();
        }}
        onBlur={() => {
          setIsFocused(false);
          onBlur?.();
        }}
      >
        {loading && (
          <TextField.Slot side="right">
            <Spinner size="1" />
          </TextField.Slot>
        )}
      </TextField.Root>
      <FieldError message={error} />
      {open && (
        <Card
          size="1"
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '2px',
            zIndex: 20,
            maxHeight: '240px',
            overflowY: 'auto',
            padding: 'var(--space-1)',
          }}
        >
          <Flex direction="column" gap="2">
            {options.map(option => (
              <Button
                key={option.id}
                type="button"
                role="option"
                aria-selected={false}
                variant="ghost"
                color="gray"
                onMouseDown={event => event.preventDefault()}
                onClick={() => onSelectOption(option)}
                style={{ justifyContent: 'flex-start', width: '100%' }}
              >
                {option.label}
              </Button>
            ))}
          </Flex>
        </Card>
      )}
    </Flex>
  );
}
